"use client";

import React from "react";
import { useRouter } from "next/navigation";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { cn } from "@/utils";
import { EDITOR_MARKER_COOKIE } from "@/lib/draft-mode";
import { useIsEditing } from "@/components/AdminSidebar";

const TINA_AUTH_TOKEN_KEY = "tinacms-auth";
const TINA_ORANGE = "#ec4816";

function hasEditorMarker() {
  return document.cookie
    .split("; ")
    .some((entry) => entry.startsWith(`${EDITOR_MARKER_COOKIE}=`));
}

function readTinaIdToken() {
  try {
    const raw = localStorage.getItem(TINA_AUTH_TOKEN_KEY);
    if (!raw) return null;
    return JSON.parse(raw)?.id_token || null;
  } catch {
    return null;
  }
}

/**
 * Draft Mode is a cookie on the whole origin, not something scoped to the Tina
 * iframe, so an editor browsing the site normally also sees unpublished content.
 * This is the only thing that tells them so, and lets them flip back to the
 * public view without logging out of Tina.
 *
 * Visibility keys off the editor marker rather than draft mode itself, so the
 * toggle stays put after switching off instead of vanishing. Public visitors
 * have no marker and get nothing.
 */
export default function DraftModeToggle({
  isEnabled,
}: {
  isEnabled: boolean;
}) {
  const router = useRouter();
  const [isEditor, setIsEditor] = React.useState(false);
  const [pending, setPending] = React.useState(false);
  const [expired, setExpired] = React.useState(false);

  const isEditing = useIsEditing();

  // Cookies are readable only after mount, so this resolves client-side rather
  // than in the server payload.
  React.useEffect(() => {
    setIsEditor(hasEditorMarker());
  }, []);

  if (!isEnabled && !isEditor) return null;
  // if (!isEditing) return null;

  const toggle = async () => {
    setPending(true);
    setExpired(false);

    try {
      if (isEnabled) {
        await fetch("/api/preview/exit", { credentials: "include" });
      } else {
        const token = readTinaIdToken();
        const response = await fetch(
          `/api/preview/enter?token=${encodeURIComponent(token ?? "")}`,
          { credentials: "include" }
        );
        // Only the admin app can refresh an expired id_token, so from a normal
        // tab the recovery is to open /admin, which re-enters on load.
        if (!response.ok) {
          setExpired(true);
          return;
        }
      }
      router.refresh();
    } catch {
      setExpired(true);
    } finally {
      setPending(false);
    }
  };

  const label = expired
    ? "Session expired, open /admin to re-enable"
    : isEnabled
      ? "Draft mode on, showing unpublished content"
      : "Draft mode off, showing the public site";

  // Always Tina orange; the icon and hover label carry the on/off state.
  const surface = { backgroundColor: TINA_ORANGE };

  return (
    <div className="group fixed top-20 left-0 z-[60] flex items-center">
      <button
        type="button"
        onClick={toggle}
        disabled={pending}
        aria-pressed={isEnabled}
        aria-label={label}
        style={surface}
        className="flex h-10 w-12 cursor-pointer items-center justify-center rounded-r-[4px] transition-opacity hover:opacity-90 disabled:cursor-wait"
      >
        {pending ? (
          <Loader2 className="mr-1 h-5 w-5 animate-spin stroke-1 text-white" />
        ) : isEnabled ? (
          <Eye className="mr-1 h-5 w-5 stroke-1 text-white" />
        ) : (
          <EyeOff className="mr-1 h-5 w-5 stroke-1 text-white" />
        )}
      </button>
      <span
        style={surface}
        className={cn(
          "max-w-0 overflow-hidden whitespace-nowrap rounded-r-[4px] py-2 text-xs font-medium text-white opacity-0 transition-all duration-200",
          "group-hover:max-w-xs group-hover:pl-2 group-hover:pr-3 group-hover:opacity-100",
          "group-focus-within:max-w-xs group-focus-within:pl-2 group-focus-within:pr-3 group-focus-within:opacity-100"
        )}
      >
        {label}
      </span>
    </div>
  );
}
